import { Container, Spinner } from "react-bootstrap";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { usePage } from "../../hooks/usePage";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import { usePrecargaWallets } from "../../hooks/usePrecargaWallets";
import useSearchParamState from "../../hooks/useSearchParamState";
import ServerTable from "../../components/ServerTable/ServerTable";

export default function PrecargasWallets() {
  usePage({ title: "Precargas - Billeteras" });

  const { eventid } = useParams();
  const navigate = useNavigate();
  const cashlessEventId = eventid ? parseInt(eventid) : null;

  const { precargaEvent, isLoadingPrecargaEvent, hasPrecargaEvent } =
    usePrecargaEvents({
      cashlessEventId,
    });

  // Pagination and filters
  const [page, setPage] = useSearchParamState("page", 1);
  const [pageSize, setPageSize] = useSearchParamState("pageSize", 20);
  const [status, setStatus] = useSearchParamState("status", "");
  const [search, setSearch] = useSearchParamState("search", "");
  const [sortBy, setSortBy] = useSearchParamState("sortBy", "created_at");
  const [sortDir, setSortDir] = useSearchParamState("sortDir", "desc");

  const { wallets, totalCount, isLoading, isError } = usePrecargaWallets({
    eventId: precargaEvent?.id,
    page: parseInt(page),
    pageSize: parseInt(pageSize),
    status,
    search,
    sortBy,
    sortDir,
  });

  if (isLoadingPrecargaEvent) {
    return (
      <Container fluid>
        <div className="text-center p-5">
          <Spinner animation="border" />
          <p className="mt-3">Cargando configuración de precarga...</p>
        </div>
      </Container>
    );
  }

  if (!eventid) {
    return null;
  }

  if (!hasPrecargaEvent) {
    return <Navigate to={`/${eventid}/preloads/create`} replace />;
  }

  const columns = [
    {
      label: "ID",
      key: "id",
      type: "number",
      width: 80,
      sortable: false,
    },
    {
      label: "Usuario",
      key: "user",
      type: "string",
      render: (row) => row.user?.email || "",
      sortable: false,
    },
    {
      label: "Saldo Cuenta",
      key: "balanceAccount",
      type: "currency",
      width: 140,
      sortKey: "balance_account",
    },
    {
      label: "Saldo Tarjeta",
      key: "balanceCard",
      type: "currency",
      width: 140,
      sortKey: "balance_card",
    },
    {
      label: "Saldo Total",
      key: "totalBalance",
      type: "currency",
      width: 140,
      sortable: false,
    },
    {
      label: "Estado Tarjeta",
      key: "cardStatus",
      type: "string",
      width: 140,
      filter: true,
      sortable: false,
    },
    {
      label: "Fecha",
      key: "createdAt",
      type: "date",
      width: 180,
      sortKey: "created_at",
    },
  ];

  const handleFiltersChange = (filters) => {
    const statusFilter = filters.find((filter) => filter.key === "cardStatus");
    const nextStatus = statusFilter?.values?.[0] ?? "";
    if (nextStatus !== status) {
      setStatus(nextStatus);
      setPage(1);
    }
  };

  return (
    <Container fluid>
      <h2>Billeteras</h2>
      <p className="text-muted mb-4">
        Lista de billeteras de usuarios con sus saldos
      </p>
      <ServerTable
        title="Billeteras"
        data={wallets}
        columns={columns}
        isLoading={isLoading}
        isError={isError}
        showExcel={true}
        showFilters={true}
        showPagination={true}
        page={parseInt(page, 10)}
        pageSize={parseInt(pageSize, 10)}
        totalCount={totalCount}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={({ sortBy: nextSortBy, sortDir: nextSortDir }) => {
          if (nextSortBy !== sortBy || nextSortDir !== sortDir) {
            setSortBy(nextSortBy);
            setSortDir(nextSortDir);
            setPage(1);
          }
        }}
        onPageChange={(nextPage) => {
          if (nextPage !== parseInt(page, 10)) setPage(nextPage);
        }}
        onPageSizeChange={(nextSize) => {
          if (nextSize !== parseInt(pageSize, 10)) {
            setPageSize(nextSize);
            setPage(1);
          }
        }}
        onFiltersChange={handleFiltersChange}
        onSelectRow={(row) => navigate(`/${eventid}/preloads/wallets/${row.id}`)}
      />
    </Container>
  );
}
